import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * Searchable combobox for car.gr makes and models.
 * - Uses native HTML <datalist> so it scales to 3000+ items with zero deps
 * - On selection, calls onPick({brand_name, make_id}) or ({model_name, model_id})
 * - Free-typed values are still accepted (no strict mode)
 */
export function CarGrMakeCombobox({ value, makeId, onPick, dataTestid }) {
  const [makes, setMakes] = useState([]);
  const [text, setText] = useState(value || "");
  const listIdRef = useRef("makes-" + Math.random().toString(36).slice(2));

  useEffect(() => {
    api.get("/cargr-lookups/makes").then((r) => setMakes(r.data.makes || []));
  }, []);

  useEffect(() => {
    setText(value || "");
  }, [value]);

  const byNameLower = useMemo(() => {
    const m = new Map();
    for (const x of makes) m.set(x.name.toLowerCase(), x);
    return m;
  }, [makes]);

  // When the brand text is set externally (e.g. by car.gr bookmarklet) and the makes list
  // is loaded, auto-resolve the make_id and emit onPick so downstream model dropdown can react.
  useEffect(() => {
    if (!text || makeId) return;
    if (makes.length === 0) return;
    const m = byNameLower.get(text.toLowerCase());
    if (m) {
      // Snap text to the canonical capitalisation from the car.gr list (e.g. "Bmw" → "BMW")
      setText(m.name);
      onPick({ brand_name: m.name, make_id: m.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makes, text]);

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    const m = byNameLower.get(v.toLowerCase());
    onPick({ brand_name: v, make_id: m ? m.id : 0 });
  };

  let statusLabel = null;
  if (makeId) {
    statusLabel = (
      <span className="text-[10px] font-mono text-[#4B5563] mt-0.5">car.gr make_id: {makeId}</span>
    );
  } else if (text) {
    statusLabel = (
      <span className="text-[10px] font-mono text-[#DC2626] mt-0.5">μη αντιστοιχισμένη — επιλέξτε από τη λίστα</span>
    );
  }

  return (
    <>
      <input
        list={listIdRef.current}
        value={text}
        onChange={onChange}
        placeholder="Πληκτρολογήστε μάρκα..."
        className="px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm"
        data-testid={dataTestid || "cargr-make-combobox"}
        autoComplete="off"
      />
      <datalist id={listIdRef.current}>
        {makes.map((m) => (
          <option key={m.id} value={m.name} label={`${m.name} (id ${m.id})`} />
        ))}
      </datalist>
      {statusLabel}
    </>
  );
}

export function CarGrModelCombobox({ value, modelId, makeId, onPick, dataTestid }) {
  const [models, setModels] = useState([]);
  const [text, setText] = useState(value || "");
  const listIdRef = useRef("models-" + Math.random().toString(36).slice(2));

  useEffect(() => {
    if (!makeId) {
      setModels([]);
      return;
    }
    api.get("/cargr-lookups/models", { params: { make_id: makeId } }).then((r) => setModels(r.data.models || []));
  }, [makeId]);

  useEffect(() => {
    setText(value || "");
  }, [value]);

  const byNameLower = useMemo(() => {
    const m = new Map();
    for (const x of models) m.set(x.name.toLowerCase(), x);
    return m;
  }, [models]);

  // Auto-resolve model_id when text is pre-filled externally and models load.
  useEffect(() => {
    if (!text || modelId) return;
    if (models.length === 0) return;
    const m = byNameLower.get(text.toLowerCase());
    if (m) {
      setText(m.name);
      onPick({ model_name: m.name, model_id: m.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models, text]);

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    const m = byNameLower.get(v.toLowerCase());
    onPick({ model_name: v, model_id: m ? m.id : 0 });
  };

  let modelStatusLabel = null;
  if (modelId) {
    modelStatusLabel = (
      <span className="text-[10px] font-mono text-[#4B5563] mt-0.5">car.gr model_id: {modelId}</span>
    );
  } else if (text) {
    modelStatusLabel = (
      <span className="text-[10px] font-mono text-[#DC2626] mt-0.5">μη αντιστοιχισμένο — επιλέξτε από τη λίστα</span>
    );
  }

  return (
    <>
      <input
        list={listIdRef.current}
        value={text}
        onChange={onChange}
        placeholder={makeId ? "Πληκτρολογήστε μοντέλο..." : "Επιλέξτε πρώτα μάρκα"}
        disabled={!makeId}
        className="px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={dataTestid || "cargr-model-combobox"}
        autoComplete="off"
      />
      <datalist id={listIdRef.current}>
        {models.map((m) => (
          <option key={m.id} value={m.name} label={`${m.name} (id ${m.id})`} />
        ))}
      </datalist>
      {modelStatusLabel}
    </>
  );
}
